import { FC, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Autocomplete, SxProps, TextField } from '@mui/material';
import { Brand } from '../../../types/Brand';
import { BrandCriteria } from '../../../types/Criteria/BrandCriteria';
import ApiService from '../../../ApiService';

interface BrandAutocompleteProps {
    value: number | string | (number | string)[] | null;
    onChange: (value: Brand | Brand[] | null) => void;
    multiple?: boolean;
    required?: boolean;
    disabled?: boolean;
    sx?: SxProps;
    label?: string;
    criteria?: BrandCriteria;
}

const BrandAutocomplete: FC<BrandAutocompleteProps> = ({
    value,
    onChange,
    label = 'Select Brand',
    multiple = false,
    required = false,
    disabled = false,
    sx = {},
    criteria = {},
}) => {
    const {
        data = [],
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['brands', criteria],
        queryFn: async () => {
            const { data } = await ApiService.getBrands(criteria);
            return data;
        },
    });

    const selectedOptions = useMemo(() => {
        if (!data) return [];
        if (Array.isArray(value)) {
            const set = new Set(value);
            return data.filter((b) => set.has(b.id) || set.has(b.name));
        }
        if (value === null || value === undefined) return [];
        return data.filter((b) => b.id === value || b.name === value);
    }, [data, value]);

    return (
        <Autocomplete
            size="small"
            multiple={multiple}
            value={selectedOptions}
            onChange={(_, newValue) => onChange(newValue as any)}
            options={data}
            loading={isLoading}
            disabled={isError || disabled}
            sx={sx}
            getOptionLabel={(option: Brand) => option.name}
            isOptionEqualToValue={(option: Brand, v: Brand) => option.id === v.id}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    inputProps={{
                        ...params.inputProps,
                        required: required && selectedOptions.length === 0,
                    }}
                />
            )}
        />
    );
};

export default BrandAutocomplete;
